import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDebounce} from '@react-hook/debounce'
import {AddressDetails, Coordinate, ShipToAddress} from '@hconnect/tuc-processes/shipToCreation'
import {useResolveAddress} from './useResolveAddress'
import {useReverseGeoCode} from './useReverseGeoCode'
import {Suggestion, useSearchAddress} from './useSearchAddress'
import {ADDRESS_SEARCH_DEBOUNCE_MS, MIN_ADDRESS_SEARCH_LENGTH} from '../constants'

interface UseAddressManagerProps {
  country: string
  initialAddress?: ShipToAddress
}

export const useAddressManager = ({country, initialAddress}: UseAddressManagerProps) => {
  const [marker, setMarker] = useState<Coordinate | null>(() => {
    if (!initialAddress) return null
    return {
      latitude: initialAddress.latitude,
      longitude: initialAddress.longitude
    }
  })

  const [searchBarText, setSearchBarText] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAddress, setSelectedAddress] = useState<Suggestion | null>(() => {
    if (initialAddress) {
      return {
        text: initialAddress.fullAddress,
        magicKey: initialAddress.fullAddress
      }
    }
    return null
  })
  const [locationToReverse, setLocationToReverse] = useState<Coordinate | null>(null)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(
    searchTerm,
    ADDRESS_SEARCH_DEBOUNCE_MS
  )
  const [suggestions, setSuggestions] = useState<Suggestion[]>(() => {
    if (initialAddress) {
      return [
        {
          text: initialAddress.fullAddress,
          magicKey: initialAddress.fullAddress
        }
      ]
    }
    return []
  })

  const addressDetailsRef = useRef<{
    addressDetails: AddressDetails
    coordinates: Coordinate
  } | null>(null)

  const reverseAddressQueryInfo = useReverseGeoCode({
    country,
    ...locationToReverse
  })

  const searchAddressQueryInfo = useSearchAddress({
    searchText: debouncedSearchTerm,
    country
  })

  const resolveAddressQueryInfo = useResolveAddress({
    country,
    searchText: selectedAddress?.text,
    magicKey: selectedAddress?.magicKey
  })

  useEffect(() => {
    if (!initialAddress) return
    const {
      latitude,
      longitude,
      street,
      streetNumber,
      postalCode,
      city,
      country,
      fullAddress,
      region
    } = initialAddress
    const coordinates = {latitude, longitude}
    const addressDetails: AddressDetails = {
      address: fullAddress,
      streetNumber: streetNumber || '',
      streetName: street,
      city,
      postalCode,
      countryCode: country,
      region
    }
    addressDetailsRef.current = {addressDetails, coordinates}
  }, [initialAddress])

  useEffect(() => {
    const searchSuggestions = searchAddressQueryInfo.data?.suggestions
    if (!searchSuggestions) return
    setSuggestions(searchSuggestions)
  }, [searchAddressQueryInfo.data])

  useEffect(() => {
    // Early return if no location to reverse or query is in progress
    if (
      !locationToReverse ||
      reverseAddressQueryInfo.isLoading ||
      reverseAddressQueryInfo.isFetching
    ) {
      return
    }

    const reversedAddress = reverseAddressQueryInfo.data?.addresses?.[0]

    // Handle case when no address is found
    if (!reversedAddress) {
      setSuggestions([])
      setSearchBarText('')
      setDebouncedSearchTerm('')
      addressDetailsRef.current = null
      setMarker(null)
      return
    }

    // Extract location and address data
    const {location, address} = reversedAddress
    const coordinates = {
      latitude: location.latitude,
      longitude: location.longitude
    }

    // Update state with resolved address data
    setMarker(coordinates)
    setSearchBarText('')
    setDebouncedSearchTerm('')
    addressDetailsRef.current = {addressDetails: address, coordinates}

    // Create and set suggestion from reversed address
    const suggestion = {
      text: address.address,
      magicKey: address.address
    }
    setSuggestions([suggestion])
    setSelectedAddress(null)
  }, [
    reverseAddressQueryInfo.data,
    reverseAddressQueryInfo.isLoading,
    reverseAddressQueryInfo.isFetching,
    locationToReverse
  ])

  useEffect(() => {
    const resolvedAddress = resolveAddressQueryInfo.data?.addresses?.[0]
    if (!resolvedAddress) return

    const {location, addressDetails} = resolvedAddress
    const coordinates = {
      latitude: location.latitude,
      longitude: location.longitude
    }
    addressDetailsRef.current = {addressDetails, coordinates}
    setMarker(coordinates)
  }, [resolveAddressQueryInfo.data])

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchTerm(query)
      setDebouncedSearchTerm(query)
      setSearchBarText(query)
      setLocationToReverse(null)
      setSelectedAddress(null)
      addressDetailsRef.current = null
      if (query.length < MIN_ADDRESS_SEARCH_LENGTH) {
        setSuggestions([])
      }
    },
    [setDebouncedSearchTerm]
  )

  const handleMapClick = useCallback((args: {lat: number; lng: number}) => {
    const coordinates = {latitude: args.lat, longitude: args.lng}
    setLocationToReverse(coordinates)
    setMarker(coordinates)
    setSelectedAddress(null)
    setSearchBarText('')
    setDebouncedSearchTerm('')
    addressDetailsRef.current = null
  }, [])

  const handleSuggestionSelect = useCallback((suggestion: Suggestion) => {
    setSelectedAddress(suggestion)
    setSuggestions([suggestion])
    setSearchBarText('')
    setDebouncedSearchTerm('')
  }, [])

  const resetAll = useCallback(() => {
    setMarker(null)
    setSearchBarText('')
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSuggestions([])
    setSelectedAddress(null)
    setLocationToReverse(null)
    addressDetailsRef.current = null
  }, [])

  const getSelectedAddressDetails = useCallback(() => {
    if (!addressDetailsRef.current) return null

    const {addressDetails, coordinates} = addressDetailsRef.current
    return {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      street: addressDetails.streetName,
      streetNumber: addressDetails.streetNumber,
      postalCode: addressDetails.postalCode,
      city: addressDetails.city,
      country,
      fullAddress: addressDetails.address,
      region: addressDetails.region
    }
  }, [country])

  const markerLocationName = useMemo(() => {
    const reversedAddress = reverseAddressQueryInfo.data?.addresses?.[0]
    const resolvedAddress = resolveAddressQueryInfo.data?.addresses?.[0]

    if (reversedAddress) {
      return reversedAddress.address.address
    }

    if (resolvedAddress) {
      return resolvedAddress.addressDetails.address
    }

    if (selectedAddress) {
      return selectedAddress.text
    }

    return undefined
  }, [reverseAddressQueryInfo.data, resolveAddressQueryInfo.data, selectedAddress])

  const isLoading = searchAddressQueryInfo.isLoading || reverseAddressQueryInfo.isFetching
  const isMarkerLoading =
    reverseAddressQueryInfo.isLoading ||
    reverseAddressQueryInfo.isFetching ||
    resolveAddressQueryInfo.isLoading ||
    resolveAddressQueryInfo.isFetching

  const canConfirm = !!(locationToReverse || selectedAddress) && !(isLoading || isMarkerLoading)

  return {
    // State
    marker,
    searchBarText,
    suggestions,
    isLoading,
    isMarkerLoading,
    canConfirm,
    markerLocationName,
    showResultList: searchBarText.length >= MIN_ADDRESS_SEARCH_LENGTH || !!locationToReverse,
    locationToReverse,

    // Actions
    handleSearchChange,
    handleMapClick,
    handleSuggestionSelect,
    getSelectedAddressDetails,
    resetAll
  }
}
